import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, Chip, Tabs, Tab, CircularProgress, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Payment, EventAvailable, ChatBubbleOutline, OpenInNew, Refresh, CheckCircle, Cancel,
} from '@mui/icons-material';
import { collectionsService } from '../../services/collectionsService';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { headerCellSx, StatusDot, StatusTone, TableEmptyState, ErrorState } from '../../components/common/pageKit';
import { RecordPaymentDialog, PromiseDialog, ActivityDialog } from './CollectionsPage';

const RISK_TONE: Record<string, StatusTone> = { HIGH: 'error', MEDIUM: 'warning', LOW: 'info', CURRENT: 'success' };
const INV_TONE: Record<string, StatusTone> = { PAID: 'success', PARTIALLY_PAID: 'info', OVERDUE: 'error', SENT: 'warning', DRAFT: 'neutral', CANCELLED: 'neutral' };
const PROMISE_TONE: Record<string, StatusTone> = { OPEN: 'info', KEPT: 'success', BROKEN: 'error', PARTIAL: 'warning' };

const Stat: React.FC<{ label: string; value: React.ReactNode; tint?: string }> = ({ label, value, tint }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>{label}</Typography>
    <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: tint || 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{value}</Typography>
  </Box>
);

const CustomerCollection360Page: React.FC = () => {
  const { customerId } = useParams();
  const id = Number(customerId);
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [promiseOpen, setPromiseOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    collectionsService.customer360(id)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load customer'))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const runAction = async (fn: () => Promise<any>, msg: string) => {
    try { await fn(); setToast(msg); load(); }
    catch (e: any) { setToast(e.message || 'Action failed'); throw e; }
  };

  if (loading && !data) return <Box sx={{ p: 6, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><ErrorState message={error} onRetry={load} /></Box>;
  if (!data) return null;

  const { profile, header } = data;
  const openInvoices = (data.invoices || []).filter((i: any) => i.status !== 'PAID' && i.status !== 'CANCELLED' && i.outstanding > 0);
  const invOpts = openInvoices.map((i: any) => ({ id: i.id, invoiceNumber: i.invoiceNumber, outstanding: i.outstanding }));

  const tabs = [
    `Invoices (${data.invoices?.length || 0})`,
    `Payments (${data.payments?.length || 0})`,
    `Credit Notes (${data.creditNotes?.length || 0})`,
    `Activities (${data.activities?.length || 0})`,
    `Promises (${data.promises?.length || 0})`,
    'Statement',
  ];

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/finance/collections')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>{profile.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {profile.gstin ? `GSTIN ${profile.gstin} · ` : ''}{profile.city || ''}{profile.state ? `, ${profile.state}` : ''}
            {profile.creditDays ? ` · ${profile.creditDays} credit days` : ''}
          </Typography>
        </Box>
        <StatusDot label={`${header.risk} risk`} tone={RISK_TONE[header.risk] || 'neutral'} />
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={load}>Refresh</Button>
      </Stack>

      {/* header stats */}
      <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
          <Stat label="Outstanding" value={formatIndianNumber(header.outstanding)} />
          <Stat label="Overdue" value={formatIndianNumber(header.overdue)} tint={header.overdue > 0 ? c.bad : undefined} />
          <Stat label="Oldest Overdue" value={header.oldestDaysOverdue > 0 ? `${header.oldestDaysOverdue}d` : '—'} />
          <Stat label="Credit Limit" value={profile.creditLimit > 0 ? formatIndianNumber(profile.creditLimit) : '—'} />
          <Stat label="Available Credit" value={profile.creditLimit > 0 ? formatIndianNumber(header.availableCredit) : '—'} tint={header.availableCredit < 0 ? c.bad : undefined} />
          <Stat label="Utilisation" value={profile.creditLimit > 0 ? `${header.utilisationPct}%` : '—'} />
          <Stat label="DSO" value={header.dso != null ? `${Math.round(header.dso)}d` : '—'} />
          <Stat label="Last Payment" value={header.lastPaymentDate ? new Date(header.lastPaymentDate).toLocaleDateString('en-IN') : 'Never'} />
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="contained" size="small" startIcon={<Payment />} onClick={() => setPayOpen(true)}>Record Payment</Button>
            <Button variant="outlined" size="small" startIcon={<EventAvailable />} onClick={() => setPromiseOpen(true)}>Promise</Button>
            <Button variant="outlined" size="small" startIcon={<ChatBubbleOutline />} onClick={() => setActivityOpen(true)}>Log Activity</Button>
          </Stack>
        </Box>
        {(header.openPromises > 0 || header.brokenPromises > 0) && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            {header.openPromises > 0 && <Chip size="small" label={`${header.openPromises} open promise${header.openPromises === 1 ? '' : 's'}`} sx={{ bgcolor: `${c.accent}14`, color: c.accent }} />}
            {header.brokenPromises > 0 && <Chip size="small" label={`${header.brokenPromises} broken promise${header.brokenPromises === 1 ? '' : 's'}`} sx={{ bgcolor: `${c.bad}14`, color: c.bad }} />}
          </Stack>
        )}
      </Paper>

      {/* tabs */}
      <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: line, px: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 } }}>
          {tabs.map((t) => <Tab key={t} label={t} />)}
        </Tabs>
        <Box sx={{ p: 0 }}>
          {tab === 0 && (
            <TableWrap>
              <TableHead><TableRow>{['Invoice', 'Date', 'Due', 'Total', 'Paid', 'Outstanding', 'Days', 'Status', ''].map((h, i) => <TableCell key={i} sx={headerCellSx} align={i >= 3 && i <= 6 ? 'right' : 'left'}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {(data.invoices || []).length === 0 ? <EmptyRow cols={9} title="No invoices" /> : data.invoices.map((iv: any) => (
                  <TableRow key={iv.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{iv.invoiceNumber}</TableCell>
                    <TableCell>{new Date(iv.invoiceDate).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>{new Date(iv.dueDate).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell align="right">{formatIndianNumber(iv.totalAmount)}</TableCell>
                    <TableCell align="right" sx={{ color: c.good }}>{formatIndianNumber(iv.paidAmount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatIndianNumber(iv.outstanding)}</TableCell>
                    <TableCell align="right" sx={{ color: iv.daysOverdue > 0 ? c.bad : 'text.secondary' }}>{iv.daysOverdue > 0 ? iv.daysOverdue : '—'}</TableCell>
                    <TableCell><StatusDot label={iv.status.replace('_', ' ')} tone={INV_TONE[iv.status] || 'neutral'} /></TableCell>
                    <TableCell align="right">
                      {iv.journalEntryId
                        ? <Tooltip title="Open journal entry"><IconButton size="small" onClick={() => navigate(`/finance/journal/${iv.journalEntryId}`)}><OpenInNew sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableWrap>
          )}
          {tab === 1 && (
            <TableWrap>
              <TableHead><TableRow>{['Date', 'Invoice', 'Amount', 'Method', 'Reference', ''].map((h, i) => <TableCell key={i} sx={headerCellSx} align={i === 2 ? 'right' : 'left'}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {(data.payments || []).length === 0 ? <EmptyRow cols={6} title="No payments recorded" /> : data.payments.map((p: any) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{new Date(p.date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{p.invoiceNumber}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: c.good }}>{formatIndianNumber(p.amount)}</TableCell>
                    <TableCell>{p.method}</TableCell>
                    <TableCell>{p.reference || '—'}</TableCell>
                    <TableCell align="right">
                      {p.journalEntryId
                        ? <Tooltip title="Open receipt journal"><IconButton size="small" onClick={() => navigate(`/finance/journal/${p.journalEntryId}`)}><OpenInNew sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableWrap>
          )}
          {tab === 2 && (
            <TableWrap>
              <TableHead><TableRow>{['Credit Note', 'Date', 'Amount', 'Status'].map((h, i) => <TableCell key={i} sx={headerCellSx} align={i === 2 ? 'right' : 'left'}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {(data.creditNotes || []).length === 0 ? <EmptyRow cols={4} title="No credit notes" /> : data.creditNotes.map((cn: any) => (
                  <TableRow key={cn.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{cn.cnNumber}</TableCell>
                    <TableCell>{new Date(cn.date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell align="right">{formatIndianNumber(cn.amount)}</TableCell>
                    <TableCell><StatusDot label={cn.status} tone={cn.status === 'POSTED' ? 'success' : 'neutral'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableWrap>
          )}
          {tab === 3 && (
            <TableWrap>
              <TableHead><TableRow>{['When', 'Type', 'Notes', 'Outcome', 'By'].map((h) => <TableCell key={h} sx={headerCellSx}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {(data.activities || []).length === 0 ? <EmptyRow cols={5} title="No activity logged yet" hint="Log a call, email or reminder to start the collection trail." /> : data.activities.map((a: any) => (
                  <TableRow key={a.id} hover>
                    <TableCell><Typography variant="caption" color="text.secondary">{new Date(a.createdAt).toLocaleString('en-IN')}</Typography></TableCell>
                    <TableCell><Chip size="small" label={a.activityType} sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${c.accent}14`, color: c.accent }} /></TableCell>
                    <TableCell sx={{ maxWidth: 320 }}><Typography variant="body2">{a.notes || '—'}</Typography></TableCell>
                    <TableCell>{a.outcome || '—'}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{a.createdBy || '—'}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableWrap>
          )}
          {tab === 4 && (
            <TableWrap>
              <TableHead><TableRow>{['Promised Date', 'Amount', 'Status', 'Notes', 'Actions'].map((h, i) => <TableCell key={i} sx={headerCellSx} align={i === 1 ? 'right' : 'left'}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {(data.promises || []).length === 0 ? <EmptyRow cols={5} title="No promises to pay" /> : data.promises.map((p: any) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{new Date(p.promisedDate).toLocaleDateString('en-IN')}{p.derivedBroken && <Typography variant="caption" sx={{ color: c.bad, display: 'block' }}>overdue</Typography>}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatIndianNumber(p.promisedAmount)}</TableCell>
                    <TableCell><StatusDot label={p.status} tone={PROMISE_TONE[p.status] || 'neutral'} /></TableCell>
                    <TableCell sx={{ maxWidth: 260 }}><Typography variant="body2">{p.notes || '—'}</Typography></TableCell>
                    <TableCell>
                      {p.status !== 'KEPT' && (
                        <Stack direction="row" spacing={0.25}>
                          <Tooltip title="Mark kept"><IconButton size="small" onClick={() => runAction(() => collectionsService.updatePromise(p.id, 'KEPT'), 'Promise marked kept')}><CheckCircle sx={{ fontSize: 17, color: c.good }} /></IconButton></Tooltip>
                          <Tooltip title="Mark broken"><IconButton size="small" onClick={() => runAction(() => collectionsService.updatePromise(p.id, 'BROKEN'), 'Promise marked broken')}><Cancel sx={{ fontSize: 17, color: c.bad }} /></IconButton></Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableWrap>
          )}
          {tab === 5 && (
            <Box sx={{ p: 2 }}>
              {!data.statement ? <TableEmptyState title="Statement unavailable" /> : (
                <>
                  <Stack direction="row" spacing={3} sx={{ mb: 2, px: 1 }}>
                    <Stat label="Total Debit" value={formatIndianNumber(data.statement.totals.debit)} />
                    <Stat label="Total Credit" value={formatIndianNumber(data.statement.totals.credit)} tint={c.good} />
                    <Stat label="Closing Balance" value={formatIndianNumber(data.statement.totals.closing)} tint={data.statement.totals.closing > 0 ? c.bad : c.good} />
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow>{['Type', 'Reference', 'Date', 'Debit', 'Credit', 'Balance'].map((h, i) => <TableCell key={i} sx={headerCellSx} align={i >= 3 ? 'right' : 'left'}>{h}</TableCell>)}</TableRow></TableHead>
                      <TableBody>
                        {data.statement.lines.length === 0 ? <EmptyRow cols={6} title="No statement lines in period" /> : data.statement.lines.map((l: any, i: number) => (
                          <TableRow key={i} hover>
                            <TableCell><Chip size="small" label={l.kind.replace('_', ' ')} sx={{ height: 20, fontSize: '0.65rem', bgcolor: l.kind === 'RECEIPT' || l.kind === 'CREDIT_NOTE' ? `${c.good}14` : `${c.accent}14`, color: l.kind === 'RECEIPT' || l.kind === 'CREDIT_NOTE' ? c.good : c.accent }} /></TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{l.ref}</TableCell>
                            <TableCell>{new Date(l.date).toLocaleDateString('en-IN')}</TableCell>
                            <TableCell align="right">{l.debit ? formatIndianNumber(l.debit) : '—'}</TableCell>
                            <TableCell align="right" sx={{ color: c.good }}>{l.credit ? formatIndianNumber(l.credit) : '—'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatIndianNumber(l.balance)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {payOpen && <RecordPaymentDialog customer={{ customerId: id, customerName: profile.name }} invoices={invOpts} onClose={() => setPayOpen(false)}
        onSubmit={(d) => runAction(() => collectionsService.recordPayment(d), 'Payment recorded').then(() => setPayOpen(false))} />}
      {promiseOpen && <PromiseDialog customer={{ customerId: id, customerName: profile.name }} invoices={invOpts} onClose={() => setPromiseOpen(false)}
        onSubmit={(d) => runAction(() => collectionsService.createPromise(d), 'Promise recorded').then(() => setPromiseOpen(false))} />}
      {activityOpen && <ActivityDialog customer={{ customerId: id, customerName: profile.name }} invoices={invOpts} onClose={() => setActivityOpen(false)}
        onSubmit={(d) => runAction(() => collectionsService.logActivity(d), 'Activity logged').then(() => setActivityOpen(false))} />}

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

const TableWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TableContainer><Table size="small">{children}</Table></TableContainer>
);
const EmptyRow: React.FC<{ cols: number; title: string; hint?: string }> = ({ cols, title, hint }) => (
  <TableRow><TableCell colSpan={cols} sx={{ border: 0 }}><TableEmptyState title={title} hint={hint} /></TableCell></TableRow>
);

export default CustomerCollection360Page;
